import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, getApiErrorMessage } from '../api';
import { Button, Card, EmptyState, Loading, Money, PageHeader, Select, Table } from '../ui';
import type {
  CompetitorComparisonData,
  VendorProposal,
  ProductMasterSearchResult,
  ProposalCompetitorDetail,
  SystemSettings,
} from '../shared/types';
import {
  calculateGiveawayEffectiveCost,
  calculateNormalizedUnitPrice,
  generateMultiCompetitorSummary,
} from '../shared/competitor-comparison';
import { calculateGrossMargin } from '../shared/domain';
import {
  COMPARISON_REPLACEMENT_LABELS,
  DEFAULT_SETTINGS,
  labelCertificateType,
} from '../shared/constants';
import { normalizeBarcodeCandidate } from '../shared/txt-parser';
import type { Notify } from '../pages';

function displayBarcodeCandidate(value: string | null | undefined): string {
  const result = normalizeBarcodeCandidate(value);
  if (result.status === 'VALID' || result.status === 'REPAIRED') return result.value;
  if (result.status === 'BARCODE_REPAIR_REQUIRED') return '條碼待修復';
  if (result.status === 'INVALID_GTIN') return '條碼格式異常';
  return '—';
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.message.includes('找不到 API')) {
      return '系統競品比對服務正在準備中，請稍候再試。';
    }
    if ((error as any).code === 'PRODUCT_NOT_FOUND' || error.message.includes('找不到指定')) {
      return '找不到指定的惠生商品，商品主檔可能已更新，請重新搜尋。';
    }
    if ((error as any).code === 'PROPOSAL_NOT_FOUND') {
      return '找不到指定的新品提案案件。';
    }
    if ((error as any).code === 'COMPETITOR_LIMIT_REACHED') {
      return '每個新品最多可選擇5個惠生既有商品進行比較。';
    }
  }
  return getApiErrorMessage(error, '操作失敗，請稍後再試。');
}

function getProposalField(proposal: any, field: string): any {
  if (!proposal) return undefined;
  if (proposal[field] !== undefined) return proposal[field];
  if (proposal.product && proposal.product[field] !== undefined) return proposal.product[field];
  if (field === 'product_name')
    return proposal.product?.name_zh || proposal.name_zh || proposal.product_name;
  if (field === 'proposal_number')
    return proposal.proposal_no || proposal.proposal_number || proposal.id;
  return undefined;
}

export function NewProductCompetitorComparePage({ notify }: { notify: Notify }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const proposalIdParam = searchParams.get('proposal_id') || searchParams.get('id') || '';

  const [proposals, setProposals] = useState<VendorProposal[]>([]);
  const [selectedProposalId, setSelectedProposalId] = useState(proposalIdParam);
  const [loadingProposals, setLoadingProposals] = useState(false);

  const [comparisonData, setComparisonData] = useState<CompetitorComparisonData | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [autoPreparing, setAutoPreparing] = useState(false);

  // 候選競品狀態
  const [candidates, setCandidates] = useState<Array<any>>([]);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [candidateTab, setCandidateTab] = useState<'RECOMMENDED' | 'MANUAL'>('RECOMMENDED');

  // 自行手動搜尋狀態
  const [manualKeyword, setManualKeyword] = useState('');
  const [manualResults, setManualResults] = useState<ProductMasterSearchResult[]>([]);
  const [searchingManual, setSearchingManual] = useState(false);
  const [hasSearchedManual, setHasSearchedManual] = useState(false);

  // 按鈕 Loading 狀態
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);
  const [addingCompetitorId, setAddingCompetitorId] = useState<string | null>(null);
  const [removingCompetitorId, setRemovingCompetitorId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  // 採購結論表單
  const [advantages, setAdvantages] = useState('');
  const [disadvantages, setDisadvantages] = useState('');
  const [replacement, setReplacement] = useState('PENDING_MEETING');
  const [conclusionNotes, setConclusionNotes] = useState('');
  const [returnRiskNotes, setReturnRiskNotes] = useState('');
  const [inventoryRiskNotes, setInventoryRiskNotes] = useState('');
  const [isPriceComp, setIsPriceComp] = useState(true);
  const [isIngredientSup, setIsIngredientSup] = useState(false);
  const [isGiveawaySup, setIsGiveawaySup] = useState(true);
  const [isMarginSup, setIsMarginSup] = useState(true);
  const [savingConclusion, setSavingConclusion] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [systemSettings, setSystemSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);

  // 載入提案清單供選擇
  useEffect(() => {
    setLoadingProposals(true);
    api.proactive
      .list()
      .then((res) => {
        setProposals(res.proposals);
        if (!selectedProposalId && res.proposals.length > 0) {
          const firstId = res.proposals[0]?.id || '';
          setSelectedProposalId(firstId);
          setSearchParams({ proposal_id: firstId });
        }
      })
      .catch((err) => notify(errorMessage(err), 'error'))
      .finally(() => setLoadingProposals(false));
  }, []);

  useEffect(() => {
    api.settings
      .get()
      .then((result) => setSystemSettings(result.settings))
      .catch(() => undefined);
  }, []);

  // 載入指定提案之競品比較資料
  const loadComparison = async (pid = selectedProposalId) => {
    if (!pid) return;
    setLoadingData(true);
    try {
      const data = await api.competitorComparisons.get(pid);
      setComparisonData(data);
      if (data.comparison) {
        setAdvantages(data.comparison.buyer_advantages || '');
        setDisadvantages(data.comparison.buyer_disadvantages || '');
        setReplacement(data.comparison.replacement_assessment || 'PENDING_MEETING');
        setConclusionNotes(data.comparison.buyer_conclusion_notes || '');
        setReturnRiskNotes(data.comparison.return_risk_notes || '');
        setInventoryRiskNotes(data.comparison.inventory_replacement_risk_notes || '');
        setIsPriceComp(Boolean(data.comparison.is_price_competitive));
        setIsIngredientSup(Boolean(data.comparison.is_ingredient_superior));
        setIsGiveawaySup(Boolean(data.comparison.is_giveaway_superior));
        setIsMarginSup(Boolean(data.comparison.is_margin_superior));
      }
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (selectedProposalId) {
      void loadComparison(selectedProposalId);
    }
  }, [selectedProposalId]);

  // 切換提案
  const handleSelectProposal = (pid: string) => {
    setSelectedProposalId(pid);
    setSearchParams({ proposal_id: pid });
  };

  // 一鍵自動準備競品比較
  const handleAutoPrepare = async () => {
    if (!selectedProposalId) return;
    setAutoPreparing(true);
    try {
      const res = await api.competitorComparisons.autoPrepare(selectedProposalId);
      if (res.top_candidates && res.top_candidates.length > 0) {
        setCandidates(res.top_candidates);
      }
      notify(res.message);
      void loadComparison();
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setAutoPreparing(false);
    }
  };

  // 搜尋候選競品
  const handleSearchCandidates = async () => {
    if (!selectedProposalId) return;
    setSearchingCandidates(true);
    try {
      const res = await api.competitorComparisons.searchCandidates(selectedProposalId);
      setCandidates(res.items);
      notify(`已搜尋出 ${res.items.length} 項推薦候選競品！`);
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setSearchingCandidates(false);
    }
  };

  // 自行搜尋惠生商品主檔庫
  const handleManualSearch = async (e?: React.FormEvent, customKw?: string) => {
    if (e) e.preventDefault();
    const kw = (customKw !== undefined ? customKw : manualKeyword).trim();
    if (!kw) {
      notify('請輸入搜尋關鍵字（商品名稱、編號、條碼或廠商）', 'error');
      return;
    }
    setSearchingManual(true);
    try {
      const res = await api.productMaster.search({
        q: kw,
        limit: '40',
      });
      setManualResults(res.items || []);
      setHasSearchedManual(true);
      if ((res.items || []).length === 0) {
        notify('查無符合條件的惠生商品主檔資料', 'info');
      } else {
        notify(`已找到 ${res.items.length} 項符合條件的惠生商品！`);
      }
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setSearchingManual(false);
    }
  };

  // 設為主要競品
  const handleSetPrimaryCompetitor = async (productId: string) => {
    if (!selectedProposalId) {
      notify('請先選擇新品提案', 'error');
      return;
    }
    setSettingPrimaryId(productId);
    try {
      const res = await api.competitorComparisons.setPrimary(selectedProposalId, productId);
      notify(res.message || '已成功設為主要競品並更新比對！');
      void loadComparison(selectedProposalId);
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setSettingPrimaryId(null);
    }
  };

  // 加入競品比較 (最多 5 個)
  const handleAddCompetitor = async (productId: string) => {
    if (!selectedProposalId) {
      notify('請先選擇新品提案', 'error');
      return;
    }
    const currentCount = comparisonData?.competitors?.length || 0;
    if (currentCount >= 5) {
      notify('每個新品最多可選擇5個惠生既有商品進行比較。', 'error');
      return;
    }

    setAddingCompetitorId(productId);
    try {
      const res = await api.competitors.add(selectedProposalId, productId);
      notify(res.message || '已加入競品清單！');
      void loadComparison(selectedProposalId);
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setAddingCompetitorId(null);
    }
  };

  // 移除競品
  const handleRemoveCompetitor = async (competitorOrProductId: string) => {
    if (!selectedProposalId) return;
    setRemovingCompetitorId(competitorOrProductId);
    try {
      const res = await api.competitors.remove(selectedProposalId, competitorOrProductId);
      notify(res.message || '已移除競品。');
      void loadComparison(selectedProposalId);
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setRemovingCompetitorId(null);
    }
  };

  // 調整競品排序 (上移 / 下移)
  const handleReorderCompetitor = async (index: number, direction: 'UP' | 'DOWN') => {
    if (!comparisonData || !selectedProposalId) return;
    const comps = [...(comparisonData.competitors || [])];
    const targetIndex = direction === 'UP' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= comps.length) return;

    const [moved] = comps.splice(index, 1);
    comps.splice(targetIndex, 0, moved);

    const orderedIds = comps.map((c) => c.competitor_record_id || c.id);
    setReordering(true);
    try {
      await api.competitorComparisons.reorder(selectedProposalId, orderedIds);
      notify('已更新競品排序！');
      void loadComparison(selectedProposalId);
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setReordering(false);
    }
  };

  // 確認/排除網路價格
  const handleToggleWebPrice = async (obsId: string, confirmed: boolean) => {
    try {
      await api.competitorComparisons.confirmWebPrice(obsId, { confirmed });
      notify(confirmed ? '已確認採納此價格！' : '已排除此價格。');
      void loadComparison();
    } catch (err) {
      notify(errorMessage(err), 'error');
    }
  };

  // 儲存採購比較結論
  const handleSaveConclusion = async () => {
    if (!selectedProposalId) return;
    setSavingConclusion(true);
    try {
      await api.competitorComparisons.saveConclusion(selectedProposalId, {
        buyer_advantages: advantages,
        buyer_disadvantages: disadvantages,
        is_price_competitive: isPriceComp,
        is_ingredient_superior: isIngredientSup,
        is_giveaway_superior: isGiveawaySup,
        is_margin_superior: isMarginSup,
        replacement_assessment: replacement,
        buyer_conclusion_notes: conclusionNotes,
        return_risk_notes: returnRiskNotes,
        inventory_replacement_risk_notes: inventoryRiskNotes,
      });
      notify('採購比較結論已成功儲存！');
      void loadComparison();
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setSavingConclusion(false);
    }
  };

  // 建立競品比較 Snapshot
  const handleCreateSnapshot = async () => {
    if (!selectedProposalId) return;
    setSnapshotting(true);
    try {
      const res = await api.competitorComparisons.createSnapshot(selectedProposalId);
      notify(res.message);
      void loadComparison();
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setSnapshotting(false);
    }
  };

  const proposal = comparisonData?.proposal;
  const competitors: ProposalCompetitorDetail[] = comparisonData?.competitors || [];
  const primaryComp =
    competitors.find((c) => c.is_primary) || comparisonData?.primary_competitor || null;
  const readiness = comparisonData?.readiness;

  const propName = getProposalField(proposal, 'product_name') || '未命名新品';
  const propBrand = getProposalField(proposal, 'brand') || '—';
  const propCategory =
    getProposalField(proposal, 'category') ||
    getProposalField(proposal, 'product_category_name') ||
    getProposalField(proposal, 'product_category_code') ||
    '';
  const propSubcategory =
    getProposalField(proposal, 'product_subcategory_name') ||
    getProposalField(proposal, 'product_subcategory_code') ||
    '—';
  const propSpec = getProposalField(proposal, 'specification') || '—';
  const propUnit =
    getProposalField(proposal, 'sales_unit') || getProposalField(proposal, 'unit') || '盒';
  const propExTax = getProposalField(proposal, 'ex_tax_price');
  const propVip =
    getProposalField(proposal, 'suggested_vip_price') || getProposalField(proposal, 'vip_price');
  const propRetail = getProposalField(proposal, 'suggested_retail_price');
  const propGiveaway = getProposalField(proposal, 'giveaway_terms');
  const propLaunchDate =
    getProposalField(proposal, 'estimated_launch_date') ||
    getProposalField(proposal, 'planned_launch_date');
  const propMonthlySales = getProposalField(proposal, 'estimated_monthly_sales');
  const propCertStatus = getProposalField(proposal, 'certificate_status');
  const propCertType = getProposalField(proposal, 'certificate_type');
  const propCertNumber = getProposalField(proposal, 'certificate_number');
  const propCertAttachmentUrl =
    getProposalField(proposal, 'certificate_attachment_url') ||
    (() => {
      const match = proposal?.attachments?.find((att: any) =>
        ['LICENSE', 'CERTIFICATION'].includes(att.type),
      );
      return match ? api.vendorProactive.downloadUrl(match.id, true) : null;
    })();
  const propCertNotRequiredReason = getProposalField(proposal, 'certificate_not_required_reason');

  // 計算新品有效進價
  const propEffectiveCostResult = calculateGiveawayEffectiveCost(propExTax, propGiveaway);
  const propEffectiveCost = propEffectiveCostResult.effectivePurchasePrice;

  // 計算新品規格單位售價
  const propUnitPrice = calculateNormalizedUnitPrice(propVip || propRetail, propSpec, propUnit);

  // 毛利試算
  const marginResult = (cost: unknown, retail: unknown) => {
    if (
      cost === null ||
      cost === undefined ||
      cost === '' ||
      retail === null ||
      retail === undefined ||
      retail === ''
    ) {
      return { amount: null, rate: null };
    }
    return calculateGrossMargin({
      exTaxPrice: Number(cost),
      suggestedRetailPrice: Number(retail),
      taxRate: systemSettings.default_tax_rate,
      method: systemSettings.margin_calculation,
    });
  };
  const marginPercent = (cost: unknown, retail: unknown): number | null => {
    const rate = marginResult(cost, retail).rate;
    return rate === null ? null : Math.round(rate * 1000) / 10;
  };
  const proposalMargin = marginPercent(propExTax, propVip);
  const proposalEffectiveMargin = marginPercent(propEffectiveCost, propVip);

  // 自動多競品決策輔助摘要
  const multiSummaryText =
    comparisonData?.multi_competitor_summary ||
    generateMultiCompetitorSummary({
      proposalName: propName,
      proposalPurchasePrice: propExTax,
      proposalEffectivePrice: propEffectiveCost,
      proposalVipPrice: propVip,
      proposalGiveawayTerms: propGiveaway,
      primaryCompetitor: primaryComp,
      competitors,
    });

  const isAtLimit = competitors.length >= 5;

  return (
    <div className="space-y-6 pb-12">
      <PageHeader
        eyebrow="COMPETITOR ANALYSIS MATRIX"
        title="新品與惠生既有商品競品比較（1～5 項競品矩陣）"
        description="支援 1～5 個惠生商品多維度並排比對，提供有效進價試算、規格單位售價、庫存去化影響及多競品整體決策輔助摘要。"
        actions={
          <div className="flex gap-2">
            <Button
              onClick={handleAutoPrepare}
              loading={autoPreparing}
              className="bg-amber-500 hover:bg-amber-600 text-ink font-bold shadow-md"
            >
              ⚡ 一鍵自動準備競品比較
            </Button>
            <Link to={`/meeting?proposal_id=${selectedProposalId}`}>
              <Button variant="secondary">進入會議模式 →</Button>
            </Link>
          </div>
        }
      />

      {/* 提案選擇列 */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <div className="grid gap-4 md:grid-cols-3 items-center">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">
              選擇比對的新品提案
            </label>
            <Select
              value={selectedProposalId}
              onChange={(e) => handleSelectProposal(e.target.value)}
              disabled={loadingProposals}
            >
              {proposals.map((p) => {
                const no = p.proposal_no || (p as any).proposal_number || p.id;
                const name = (p as any).product_name || p.product?.name_zh || '新品提案';
                return (
                  <option key={p.id} value={p.id}>
                    [{no}] {name} - {p.vendor_name || '廠商'}
                  </option>
                );
              })}
            </Select>
          </div>

          {/* 完成度進度條 */}
          {readiness && (
            <div className="md:col-span-2 space-y-1.5">
              <div className="flex justify-between items-center text-xs">
                <span className="font-bold text-slate-700">
                  競品比較完成度：
                  <strong className="text-teal-700 font-mono text-sm ml-1">
                    {readiness.percentage}%
                  </strong>
                </span>
                <span className="text-slate-500">
                  已選內部競品：
                  <strong className="text-teal-800 font-mono">{competitors.length} / 5</strong>
                  {isAtLimit && (
                    <span className="ml-1 text-[11px] text-amber-700 font-bold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                      已達上限 5 個
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-300"
                  style={{ width: `${readiness.percentage}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      {loadingData ? (
        <Loading label="載入新品與多競品比較資料中…" />
      ) : !proposal ? (
        <EmptyState
          title="請選擇新品提案"
          description="從上方下拉選單中選擇一筆新品提案以展開 1～5 個惠生競品比對。"
        />
      ) : (
        <div className="space-y-6">
          {/* SECTION 1: 本次新品提案完整摘要卡 */}
          <Card className="p-6 border-teal-200 bg-teal-50/20">
            <div className="flex items-center justify-between border-b border-teal-100 pb-3 mb-4">
              <div>
                <span className="text-xs font-bold text-teal-700 uppercase tracking-wide">
                  SOURCE A: 本次新品提案
                </span>
                <h2 className="text-xl font-extrabold text-ink mt-0.5">{propName}</h2>
                <p className="text-xs text-slate-500">
                  品牌：{propBrand} ｜ 分類：{propCategory} ｜ 規格：{propSpec} ｜ 單位：{propUnit}
                </p>
                {propCertStatus === 'HAS_CERTIFICATE' ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded bg-teal-100 px-2 py-0.5 font-bold text-teal-800 border border-teal-200">
                      ✓ 有認證／許可證
                    </span>
                    <span className="font-semibold text-slate-700">
                      {propCertType ? labelCertificateType(propCertType) : ''}
                    </span>
                    <span className="font-mono text-slate-600 bg-white px-2 py-0.5 rounded border border-slate-200">
                      {propCertNumber || '—'}
                    </span>
                    {propCertAttachmentUrl && (
                      <a
                        href={propCertAttachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="font-bold text-teal-700 hover:underline inline-flex items-center gap-1 bg-white px-2 py-0.5 rounded border border-teal-200"
                      >
                        👁️ 查看許可證文件
                      </a>
                    )}
                  </div>
                ) : propCertStatus === 'NOT_REQUIRED' ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded bg-slate-200 px-2 py-0.5 font-bold text-slate-700">
                      免認證／許可證
                    </span>
                    <span className="text-slate-500">
                      {propCertNotRequiredReason || '依法無需認證'}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400">提案編號</div>
                <div className="font-mono font-bold text-slate-700">
                  {getProposalField(proposal, 'proposal_number')}
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-4 text-xs">
              <div className="p-3 bg-white rounded-xl border border-slate-100">
                <span className="text-slate-400 block font-bold">牌價進價 (未稅)</span>
                <span className="text-base font-bold text-ink mt-0.5 block">
                  <Money value={propExTax} />
                </span>
                <span className="text-[11px] text-slate-500 block mt-1">
                  單位進價：{propUnitPrice.display}
                </span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-slate-100">
                <span className="text-slate-400 block font-bold">搭贈後有效進價</span>
                <span className="text-base font-extrabold text-emerald-700 mt-0.5 block">
                  {propEffectiveCost !== null ? (
                    <Money value={propEffectiveCost} />
                  ) : (
                    '有效進價待確認'
                  )}
                </span>
                <span className="text-[11px] text-emerald-600 block mt-1">
                  {propEffectiveCostResult.isCalculated
                    ? `搭贈折讓 ${propEffectiveCostResult.discountPercentage}%`
                    : propEffectiveCostResult.display}
                </span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-slate-100">
                <span className="text-slate-400 block font-bold">建議 VIP 價 / 建議售價</span>
                <span className="text-base font-bold text-teal-700 mt-0.5 block">
                  <Money value={propVip} />
                  <span className="text-xs text-slate-400 ml-1 font-normal">
                    / <Money value={propRetail} />
                  </span>
                </span>
                <span className="text-[11px] text-teal-700 block mt-1 font-semibold">
                  預估毛利：{proposalMargin != null ? `${proposalMargin}%` : '—'}
                  {proposalEffectiveMargin != null && ` (有效毛利 ${proposalEffectiveMargin}%)`}
                </span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-slate-100">
                <span className="text-slate-400 block font-bold">預估上市日 / 月銷量</span>
                <span className="font-bold text-slate-700 mt-0.5 block">
                  {propLaunchDate || '未定'} ｜ {propMonthlySales ? `${propMonthlySales} 件` : '—'}
                </span>
                <span className="text-[11px] text-slate-500 block mt-1">
                  搭贈：{propGiveaway || '無搭贈條件'}
                </span>
              </div>
            </div>
          </Card>

          {/* SECTION 2: 已選競品管理列 (1~5 個商品，排序與主要競品切換) */}
          <Card className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3 mb-4">
              <div>
                <h3 className="text-base font-bold text-ink flex items-center gap-2">
                  <span>已選內部競品清單</span>
                  <span className="rounded-full bg-teal-100 text-teal-800 text-xs px-2.5 py-0.5 font-bold font-mono">
                    {competitors.length} / 5
                  </span>
                  {primaryComp ? (
                    <span className="text-xs text-slate-500">
                      主要競品：
                      <strong className="text-teal-800 font-bold ml-1">
                        {primaryComp.product_name}
                      </strong>
                    </span>
                  ) : (
                    <span className="text-xs text-amber-700 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                      ⚠️ 尚未設定主要競品，請從下方指定
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  可選擇 1～5 個惠生商品進行並排比較；可隨時調整排序（▲/▼）或切換主要競品。
                </p>
              </div>

              {isAtLimit && (
                <div className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-xl">
                  每個新品最多可選擇5個惠生既有商品進行比較。
                </div>
              )}
            </div>

            {competitors.length === 0 ? (
              <div className="py-6 text-center text-slate-400 text-xs">
                尚未加入任何惠生既有競品。請從下方「智慧優先級推薦」或「自行搜尋商品主檔」加入 1～5
                個競品。
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {competitors.map((comp, idx) => {
                  const isPrimary = Boolean(comp.is_primary);
                  const eff = calculateGiveawayEffectiveCost(
                    comp.current_purchase_price ?? comp.average_cost,
                    comp.current_vendor_giveaway_terms,
                  );

                  return (
                    <div
                      key={comp.id || idx}
                      className={`p-4 rounded-xl border transition ${
                        isPrimary
                          ? 'border-teal-500 bg-teal-50/40 ring-2 ring-teal-500/20 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-teal-300'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
                            #{idx + 1}
                          </span>
                          <span className="font-mono text-xs font-bold text-teal-800">
                            {comp.product_code || comp.itemId}
                          </span>
                        </div>
                        {isPrimary ? (
                          <span className="px-2 py-0.5 bg-teal-600 text-white rounded-full text-[11px] font-bold shadow-2xs">
                            ★ 主要競品
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[11px] font-medium">
                            比較商品
                          </span>
                        )}
                      </div>

                      <h4
                        className="font-bold text-ink text-sm mt-1.5 truncate"
                        title={comp.product_name}
                      >
                        {comp.product_name}
                      </h4>
                      <p className="text-xs text-slate-500 truncate">
                        規格：{comp.specification || '—'} ｜ 庫存：{comp.warehouse_stock ?? 0}{' '}
                        {comp.sales_unit || '件'}
                      </p>

                      <div className="mt-2.5 pt-2.5 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-400 block text-[11px]">進價／有效進價</span>
                          <span className="font-bold text-ink">
                            <Money value={comp.current_purchase_price ?? comp.average_cost} />
                          </span>
                          {eff.isCalculated && eff.effectivePurchasePrice && (
                            <span className="text-[11px] text-emerald-700 block font-semibold">
                              有效: <Money value={eff.effectivePurchasePrice} />
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[11px]">VIP 價</span>
                          <span className="font-bold text-teal-700">
                            <Money value={comp.vip_price ?? comp.regular_sale_price} />
                          </span>
                        </div>
                      </div>

                      {/* 操作列：排序、設主要、移除 */}
                      <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={idx === 0 || reordering}
                            onClick={() => void handleReorderCompetitor(idx, 'UP')}
                            className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                            title="上移"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            disabled={idx === competitors.length - 1 || reordering}
                            onClick={() => void handleReorderCompetitor(idx, 'DOWN')}
                            className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs"
                            title="下移"
                          >
                            ▼
                          </button>
                        </div>

                        <div className="flex items-center gap-1.5">
                          {!isPrimary && (
                            <button
                              type="button"
                              onClick={() => void handleSetPrimaryCompetitor(comp.id)}
                              disabled={Boolean(settingPrimaryId)}
                              className="px-2 py-1 bg-white border border-teal-300 text-teal-800 hover:bg-teal-50 rounded text-[11px] font-bold transition"
                            >
                              ★ 設為主要
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              void handleRemoveCompetitor(comp.competitor_record_id || comp.id)
                            }
                            disabled={Boolean(removingCompetitorId)}
                            className="px-2 py-1 bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 rounded text-[11px] font-semibold transition"
                          >
                            移除
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* SECTION 3: 惠生既有商品挑選與加入來源 (智慧推薦 + 手動搜尋) */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5 border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-bold text-ink">
                  挑選加入內部競品（目前 {competitors.length} / 5）
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  可使用 12 項優先級演算法智慧推薦，或直接搜尋全品項商品主檔庫（11,551 筆）加入。
                </p>
              </div>

              {/* 分頁切換 */}
              <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setCandidateTab('RECOMMENDED');
                    if (candidates.length === 0) void handleSearchCandidates();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    candidateTab === 'RECOMMENDED'
                      ? 'bg-white text-teal-800 shadow-sm'
                      : 'text-slate-600 hover:text-ink'
                  }`}
                >
                  ⚡ 智慧優先級推薦 {candidates.length > 0 ? `(${candidates.length})` : ''}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCandidateTab('MANUAL');
                    if (!hasSearchedManual && propCategory) {
                      void handleManualSearch(undefined, propCategory);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    candidateTab === 'MANUAL'
                      ? 'bg-white text-teal-800 shadow-sm'
                      : 'text-slate-600 hover:text-ink'
                  }`}
                >
                  🔍 自行搜尋商品主檔 {manualResults.length > 0 ? `(${manualResults.length})` : ''}
                </button>
              </div>
            </div>

            {/* TAB 1: 智慧推薦 */}
            {candidateTab === 'RECOMMENDED' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-slate-500">
                    比對條碼、子分類、品牌、用途、規格與價格帶，依 12 項規則排序推薦。
                  </span>
                  <Button
                    variant="secondary"
                    onClick={handleSearchCandidates}
                    loading={searchingCandidates}
                  >
                    🔄 重新計算智慧推薦
                  </Button>
                </div>

                {candidates.length > 0 ? (
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {candidates.map((cand, idx) => {
                      const item = cand.candidate || (cand as any);
                      const candId = item.id;
                      const candCode = item.product_code || '—';
                      const candName = item.product_name || '未命名商品';
                      const candSpec = item.specification || item.sales_unit || '—';
                      const candVip = item.vip_price ?? item.regular_sale_price;
                      const score = cand.score ?? (cand as any).similarityPercentage ?? 0;
                      const reasons = cand.reasons || (cand as any).matchReasons || [];
                      const isAlreadyAdded = competitors.some(
                        (c) => c.id === candId || c.product_id === candId,
                      );
                      const isPrimary = primaryComp?.id === candId;

                      return (
                        <div
                          key={candId || idx}
                          className={`p-4 rounded-xl border transition ${
                            isPrimary
                              ? 'border-teal-500 bg-teal-50/30 ring-2 ring-teal-500/20'
                              : isAlreadyAdded
                                ? 'border-slate-300 bg-slate-50/50'
                                : 'border-slate-200 bg-white hover:border-teal-300'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <span className="font-mono text-xs font-bold text-teal-700">
                              {candCode}
                            </span>
                            <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full text-xs font-extrabold">
                              相似度 {score}%
                            </span>
                          </div>
                          <h4 className="font-bold text-ink text-sm mt-1 truncate" title={candName}>
                            {candName}
                          </h4>
                          <p className="text-xs text-slate-500 truncate">{candSpec}</p>

                          <div className="flex flex-wrap gap-1 mt-2">
                            {reasons.map((r: string, ri: number) => (
                              <span
                                key={ri}
                                className="bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0.5 rounded"
                              >
                                {r}
                              </span>
                            ))}
                          </div>

                          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center text-xs">
                            <div>
                              <span className="text-slate-400">VIP價：</span>
                              <span className="font-bold text-ink">
                                <Money value={candVip} />
                              </span>
                            </div>
                            {isPrimary ? (
                              <span className="text-teal-700 font-bold text-xs">✓ ★ 主要競品</span>
                            ) : isAlreadyAdded ? (
                              <span className="text-slate-500 font-semibold text-xs">
                                ✓ 已在比較清單
                              </span>
                            ) : (
                              <div className="flex gap-1.5">
                                <Button
                                  variant="secondary"
                                  className="text-xs px-2.5 py-1 min-h-7"
                                  onClick={() => handleAddCompetitor(candId)}
                                  loading={addingCompetitorId === candId}
                                  disabled={isAtLimit || Boolean(addingCompetitorId)}
                                  title={
                                    isAtLimit
                                      ? '每個新品最多可選擇5個惠生既有商品進行比較。'
                                      : undefined
                                  }
                                >
                                  + 加入比較
                                </Button>
                                <Button
                                  variant="primary"
                                  className="text-xs px-2.5 py-1 min-h-7 bg-teal-700 hover:bg-teal-800 text-white"
                                  onClick={() => handleSetPrimaryCompetitor(candId)}
                                  loading={settingPrimaryId === candId}
                                  disabled={Boolean(settingPrimaryId)}
                                >
                                  ★ 設主要
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400 text-xs">
                    尚未計算候選競品。請點擊「⚡ 一鍵自動準備競品比較」或「🔄 重新計算智慧推薦」。
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: 自行搜尋與手動挑選 */}
            {candidateTab === 'MANUAL' && (
              <div className="space-y-4">
                <form
                  onSubmit={(e) => void handleManualSearch(e)}
                  className="flex flex-col sm:flex-row gap-2"
                >
                  <div className="relative flex-1">
                    <input
                      type="text"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm text-ink placeholder-slate-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                      placeholder="輸入商品名稱、8碼商品代碼、國際條碼或供應商名稱..."
                      value={manualKeyword}
                      onChange={(e) => setManualKeyword(e.target.value)}
                    />
                    {manualKeyword && (
                      <button
                        type="button"
                        onClick={() => setManualKeyword('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <Button type="submit" loading={searchingManual}>
                    🔍 搜尋商品主檔
                  </Button>
                </form>

                {/* 快速關鍵字建議 */}
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                  <span>建議關鍵字：</span>
                  {propCategory && (
                    <button
                      type="button"
                      onClick={() => {
                        setManualKeyword(propCategory);
                        void handleManualSearch(undefined, propCategory);
                      }}
                      className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-teal-50 hover:text-teal-700 transition"
                    >
                      {propCategory}
                    </button>
                  )}
                  {propBrand && (
                    <button
                      type="button"
                      onClick={() => {
                        setManualKeyword(propBrand);
                        void handleManualSearch(undefined, propBrand);
                      }}
                      className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-teal-50 hover:text-teal-700 transition"
                    >
                      {propBrand}
                    </button>
                  )}
                  {propName && (
                    <button
                      type="button"
                      onClick={() => {
                        const kw = propName.slice(0, 4);
                        setManualKeyword(kw);
                        void handleManualSearch(undefined, kw);
                      }}
                      className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-teal-50 hover:text-teal-700 transition"
                    >
                      {propName.slice(0, 4)}
                    </button>
                  )}
                </div>

                {manualResults.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <Table>
                      <thead>
                        <tr className="bg-slate-50 text-slate-700 text-xs">
                          <th className="p-3 text-left">商品代號／條碼</th>
                          <th className="p-3 text-left">商品名稱</th>
                          <th className="p-3 text-left">分類／規格</th>
                          <th className="p-3 text-left">供應商</th>
                          <th className="p-3 text-right">VIP價 / 售價</th>
                          <th className="p-3 text-right">進價 / 均價</th>
                          <th className="p-3 text-center">庫存</th>
                          <th className="p-3 text-right">加入操作</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs">
                        {manualResults.map((item) => {
                          const isAlreadyAdded = competitors.some(
                            (c) => c.id === item.id || c.product_id === item.id,
                          );
                          const isPrimary = primaryComp?.id === item.id;

                          return (
                            <tr key={item.id} className="hover:bg-slate-50/60 transition">
                              <td className="p-3 font-mono font-bold text-teal-800">
                                <div>{item.itemId || '—'}</div>
                                {item.barcode && (
                                  <div className="text-[10px] text-slate-400 font-normal">
                                    {item.barcode}
                                  </div>
                                )}
                              </td>
                              <td className="p-3 font-bold text-ink">
                                <div>{item.productName || '未命名商品'}</div>
                                {item.englishName && (
                                  <div className="text-[11px] text-slate-400 font-normal">
                                    {item.englishName}
                                  </div>
                                )}
                              </td>
                              <td className="p-3 text-slate-600">
                                <div>
                                  {item.categoryName || item.categoryCode || '—'}
                                  {item.subcategoryName && ` > ${item.subcategoryName}`}
                                </div>
                                <div className="text-slate-400 text-[11px]">
                                  {item.spec || '—'} ｜ {item.salesUnit || '—'}
                                </div>
                              </td>
                              <td className="p-3 text-slate-600">
                                <div>{item.supplierName || '—'}</div>
                                {item.supplierCode && (
                                  <div className="text-[10px] text-slate-400">
                                    {item.supplierCode}
                                  </div>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                <span className="font-bold text-teal-800 block">
                                  {item.vipPrice != null ? <Money value={item.vipPrice} /> : '—'}
                                </span>
                                <span className="text-slate-400 text-[11px] block">
                                  定價:{' '}
                                  {item.salePrice != null ? <Money value={item.salePrice} /> : '—'}
                                </span>
                              </td>
                              <td className="p-3 text-right">
                                <span className="font-bold text-ink block">
                                  {item.purchasePrice != null ? (
                                    <Money value={item.purchasePrice} />
                                  ) : (
                                    '—'
                                  )}
                                </span>
                                <span className="text-slate-400 text-[11px] block">
                                  均:{' '}
                                  {item.averageCost != null ? (
                                    <Money value={item.averageCost} />
                                  ) : (
                                    '—'
                                  )}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                {item.stock != null ? (
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                      item.stock > 0
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : item.stock < 0
                                          ? 'bg-rose-50 text-rose-700'
                                          : 'bg-slate-100 text-slate-500'
                                    }`}
                                  >
                                    {item.stock}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-3 text-right whitespace-nowrap">
                                {isPrimary ? (
                                  <span className="inline-flex items-center gap-1 text-teal-700 font-bold text-xs bg-teal-50 px-2.5 py-1 rounded-lg border border-teal-200">
                                    ★ 目前主要競品
                                  </span>
                                ) : isAlreadyAdded ? (
                                  <span className="text-slate-500 font-semibold text-xs">
                                    ✓ 已加入比較
                                  </span>
                                ) : (
                                  <div className="flex items-center justify-end gap-1.5">
                                    <Button
                                      variant="secondary"
                                      className="text-xs px-2.5 py-1 min-h-7"
                                      onClick={() => handleAddCompetitor(item.id)}
                                      loading={addingCompetitorId === item.id}
                                      disabled={
                                        isAtLimit ||
                                        !item.id ||
                                        Boolean(settingPrimaryId || addingCompetitorId)
                                      }
                                      title={
                                        isAtLimit
                                          ? '每個新品最多可選擇5個惠生既有商品進行比較。'
                                          : undefined
                                      }
                                    >
                                      + 加入比較
                                    </Button>
                                    <Button
                                      variant="primary"
                                      className="text-xs px-2.5 py-1 min-h-7 bg-teal-700 hover:bg-teal-800 text-white"
                                      onClick={() => handleSetPrimaryCompetitor(item.id)}
                                      loading={settingPrimaryId === item.id}
                                      disabled={
                                        !item.id || Boolean(settingPrimaryId || addingCompetitorId)
                                      }
                                    >
                                      ★ 設主要
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                ) : hasSearchedManual ? (
                  <div className="py-8 text-center text-slate-400 text-xs">
                    查無符合條件的商品，請嘗試輸入更短或不同的關鍵字搜尋。
                  </div>
                ) : (
                  <div className="py-8 text-center text-slate-400 text-xs">
                    請輸入商品關鍵字或代碼後點擊「🔍 搜尋商品主檔」以手動挑選競品。
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* SECTION 4: 1 + N 多欄位並排比較表 (1 個新品 + 1~5 個惠生競品矩陣) */}
          <Card className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-ink">
                  新品與惠生競品完整比對矩陣（1 + {competitors.length} 欄位）
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  涵蓋商品基本資料、價格與毛利、營運庫存退貨、商品內容 4 大面向指標。
                </p>
              </div>
              <span className="text-xs text-slate-500 font-mono">
                顯示欄數：本次新品 + {competitors.length} 項惠生既有競品
              </span>
            </div>

            {competitors.length === 0 ? (
              <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                <p className="text-sm font-bold text-slate-600">
                  目前尚未選擇任何惠生既有商品進行比較
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  請由上方「智慧優先級推薦」或「自行搜尋商品主檔」挑選 1～5
                  個內部競品以繪製完整比對矩陣。
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <Table>
                  <thead>
                    <tr className="bg-slate-100/90 text-slate-800 text-xs">
                      <th className="p-3 w-40 min-w-[140px] text-left font-extrabold">
                        比較維度與項目
                      </th>
                      {/* 新品欄 */}
                      <th className="p-3 min-w-[220px] bg-teal-50/80 border-l border-r border-teal-200 text-left">
                        <div className="text-[11px] uppercase tracking-wider font-extrabold text-teal-800">
                          🌟 本次新品提案
                        </div>
                        <div className="font-extrabold text-ink text-sm mt-0.5">{propName}</div>
                      </th>
                      {/* 競品 1 ~ 5 欄 */}
                      {competitors.map((comp, cIdx) => (
                        <th
                          key={comp.id || cIdx}
                          className={`p-3 min-w-[220px] text-left ${
                            comp.is_primary
                              ? 'bg-amber-50/70 border-l border-r border-amber-300'
                              : 'bg-slate-50 border-l border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold text-slate-500">
                              競品 #{cIdx + 1}
                            </span>
                            {comp.is_primary ? (
                              <span className="px-2 py-0.5 bg-teal-600 text-white rounded text-[10px] font-bold">
                                ★ 主要競品
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.2 bg-slate-200 text-slate-600 rounded text-[10px]">
                                比較商品
                              </span>
                            )}
                          </div>
                          <div
                            className="font-bold text-ink text-sm mt-0.5 truncate"
                            title={comp.product_name}
                          >
                            {comp.product_name}
                          </div>
                          <div className="font-mono text-[11px] text-slate-500 font-normal">
                            {comp.product_code || comp.itemId}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 text-xs">
                    {/* ===== CATEGORY 1: 商品資料 ===== */}
                    <tr className="bg-slate-100/60 font-extrabold text-slate-700">
                      <td colSpan={2 + competitors.length} className="p-2.5">
                        📦 一、商品基本資料
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">商品代號</td>
                      <td className="p-3 bg-teal-50/20 font-mono font-bold text-teal-900 border-l border-r border-teal-100">
                        {getProposalField(proposal, 'proposal_number')}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 font-mono font-bold ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.product_code || c.itemId || '—'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">商品名稱</td>
                      <td className="p-3 bg-teal-50/20 font-bold text-ink border-l border-r border-teal-100">
                        {propName}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 font-bold text-ink ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.product_name}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">品牌</td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {propBrand}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.brand || '—'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">
                        商品分類／子分類
                      </td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {propCategory || '—'} {propSubcategory !== '—' && `> ${propSubcategory}`}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.imported_category_name || c.product_category_code || '—'}
                          {c.imported_subcategory_name && ` > ${c.imported_subcategory_name}`}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">
                        規格與銷售單位
                      </td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {propSpec} ｜ {propUnit}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.specification || '—'} ｜ {c.sales_unit || '盒'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">供應商</td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {proposal.vendor_name || '—'}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.imported_supplier_name || c.legacy_vendor_code || '—'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">國際條碼</td>
                      <td className="p-3 bg-teal-50/20 font-mono border-l border-r border-teal-100">
                        {displayBarcodeCandidate(proposal.barcode)}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 font-mono ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {displayBarcodeCandidate(c.primary_barcode_candidate)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">
                        認證／主管機關字號
                      </td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {propCertStatus === 'HAS_CERTIFICATE' ? (
                          <div className="flex flex-col gap-1">
                            <span className="font-mono text-teal-800 font-bold">
                              {propCertNumber || '有證'} ({labelCertificateType(propCertType)})
                            </span>
                            {propCertAttachmentUrl && (
                              <a
                                href={propCertAttachmentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] font-bold text-teal-600 hover:underline inline-flex items-center gap-1"
                              >
                                👁️ 查看附件
                              </a>
                            )}
                          </div>
                        ) : propCertStatus === 'NOT_REQUIRED' ? (
                          <span className="text-slate-500">
                            免認證
                            {propCertNotRequiredReason ? ` (${propCertNotRequiredReason})` : ''}
                          </span>
                        ) : (
                          <span className="text-amber-700">未填寫</span>
                        )}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.record_type === 'PRODUCT' ? '既有主檔合格商品' : '—'}
                        </td>
                      ))}
                    </tr>

                    {/* ===== CATEGORY 2: 價格 ===== */}
                    <tr className="bg-slate-100/60 font-extrabold text-slate-700">
                      <td colSpan={2 + competitors.length} className="p-2.5">
                        💰 二、價格與毛利分析
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">
                        目前牌價進價 (未稅)
                      </td>
                      <td className="p-3 bg-teal-50/20 font-bold text-ink border-l border-r border-teal-100">
                        <Money value={propExTax} />
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 font-bold text-ink ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.current_purchase_price ? (
                            <Money value={c.current_purchase_price} />
                          ) : (
                            <span className="text-amber-700">
                              0 元 (均 <Money value={c.average_cost} />)
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">
                        平均成本 (移動平均)
                      </td>
                      <td className="p-3 bg-teal-50/20 text-slate-400 border-l border-r border-teal-100">
                        新品尚無庫存成本
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.average_cost != null ? <Money value={c.average_cost} /> : '—'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">廠商搭贈條件</td>
                      <td className="p-3 bg-teal-50/20 font-semibold text-emerald-800 border-l border-r border-teal-100">
                        {propGiveaway || '無搭贈條件'}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.current_vendor_giveaway_terms || '無搭贈條件'}
                        </td>
                      ))}
                    </tr>
                    <tr className="bg-emerald-50/30">
                      <td className="p-3 font-extrabold text-emerald-950 bg-emerald-100/50">
                        ⭐ 搭贈後有效進價
                      </td>
                      <td className="p-3 bg-teal-50/40 font-extrabold text-emerald-800 text-sm border-l border-r border-teal-200">
                        {propEffectiveCost !== null ? (
                          <Money value={propEffectiveCost} />
                        ) : (
                          '有效進價待確認'
                        )}
                        <span className="text-[11px] text-emerald-600 block font-normal mt-0.5">
                          {propEffectiveCostResult.isCalculated
                            ? `（${propEffectiveCostResult.display}）`
                            : '有效進價待確認'}
                        </span>
                      </td>
                      {competitors.map((c) => {
                        const eff = calculateGiveawayEffectiveCost(
                          c.current_purchase_price ?? c.average_cost,
                          c.current_vendor_giveaway_terms,
                        );
                        return (
                          <td
                            key={c.id}
                            className={`p-3 font-extrabold text-sm ${
                              c.is_primary
                                ? 'bg-amber-50/40 text-amber-900 border-l border-r border-amber-200'
                                : 'text-slate-800'
                            }`}
                          >
                            {eff.effectivePurchasePrice !== null ? (
                              <Money value={eff.effectivePurchasePrice} />
                            ) : (
                              '有效進價待確認'
                            )}
                            <span className="text-[11px] text-slate-500 block font-normal mt-0.5">
                              {eff.isCalculated ? eff.display : '有效進價待確認'}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">
                        建議 VIP 價 / 一般售價
                      </td>
                      <td className="p-3 bg-teal-50/20 font-bold text-teal-800 border-l border-r border-teal-100">
                        <Money value={propVip} />
                        <span className="text-slate-400 text-xs ml-1">
                          / <Money value={propRetail} />
                        </span>
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 font-bold text-slate-800 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          <Money value={c.vip_price} />
                          <span className="text-slate-400 text-xs ml-1">
                            / <Money value={c.regular_sale_price} />
                          </span>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">員工價</td>
                      <td className="p-3 bg-teal-50/20 text-slate-400 border-l border-r border-teal-100">
                        —
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.employee_price != null ? <Money value={c.employee_price} /> : '—'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">規格單位售價</td>
                      <td className="p-3 bg-teal-50/20 font-semibold text-teal-800 border-l border-r border-teal-100">
                        {propUnitPrice.display}
                      </td>
                      {competitors.map((c) => {
                        const unitP = calculateNormalizedUnitPrice(
                          c.vip_price ?? c.regular_sale_price,
                          c.specification,
                          c.sales_unit,
                        );
                        return (
                          <td
                            key={c.id}
                            className={`p-3 font-semibold ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                          >
                            {unitP.display}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">
                        預估毛利額 (VIP - 進價)
                      </td>
                      <td className="p-3 bg-teal-50/20 font-bold text-emerald-800 border-l border-r border-teal-100">
                        {marginResult(propExTax, propVip).amount !== null ? (
                          <Money value={marginResult(propExTax, propVip).amount} />
                        ) : (
                          '—'
                        )}
                      </td>
                      {competitors.map((c) => {
                        const price = c.current_purchase_price ?? c.average_cost;
                        const vip = c.vip_price ?? c.regular_sale_price;
                        return (
                          <td
                            key={c.id}
                            className={`p-3 font-bold ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                          >
                            {marginResult(price, vip).amount !== null ? (
                              <Money value={marginResult(price, vip).amount} />
                            ) : (
                              '—'
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">預估毛利率</td>
                      <td className="p-3 bg-teal-50/20 font-extrabold text-emerald-700 text-sm border-l border-r border-teal-100">
                        {proposalMargin !== null ? `${proposalMargin}%` : '—'}
                      </td>
                      {competitors.map((c) => {
                        const price = c.current_purchase_price ?? c.average_cost;
                        const vip = c.vip_price ?? c.regular_sale_price;
                        const margin = marginPercent(price, vip);
                        return (
                          <td
                            key={c.id}
                            className={`p-3 font-extrabold text-sm ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100 text-amber-900' : 'text-slate-700'}`}
                          >
                            {margin !== null ? `${margin}%` : '—'}
                          </td>
                        );
                      })}
                    </tr>

                    {/* ===== CATEGORY 3: 營運 ===== */}
                    <tr className="bg-slate-100/60 font-extrabold text-slate-700">
                      <td colSpan={2 + competitors.length} className="p-2.5">
                        🏭 三、營運與庫存狀態
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">目前總倉庫存</td>
                      <td className="p-3 bg-teal-50/20 text-slate-400 border-l border-r border-teal-100">
                        0 (新品未進貨)
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 font-bold ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          <span
                            className={`px-2 py-0.5 rounded-full ${
                              (c.warehouse_stock || 0) > 0
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {c.warehouse_stock ?? 0} {c.sales_unit || '件'}
                          </span>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">已訂未到量</td>
                      <td className="p-3 bg-teal-50/20 text-slate-400 border-l border-r border-teal-100">
                        —
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.on_order_quantity
                            ? `${c.on_order_quantity} ${c.sales_unit || '件'}`
                            : '0'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">是否可退貨</td>
                      <td className="p-3 bg-teal-50/20 font-semibold border-l border-r border-teal-100">
                        {proposal.terms?.return_allowed || '依合約'}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 font-semibold ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.return_allowed || '可退'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">退換貨條件</td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {proposal.terms?.return_terms ||
                          proposal.terms?.return_conditions ||
                          '依提案規範'}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.return_conditions || '標準退貨條件'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">商品狀態</td>
                      <td className="p-3 bg-teal-50/20 font-bold text-sky-800 border-l border-r border-teal-100">
                        待新品會議審查
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs">
                            正常在售
                          </span>
                        </td>
                      ))}
                    </tr>

                    {/* ===== CATEGORY 4: 商品內容 ===== */}
                    <tr className="bg-slate-100/60 font-extrabold text-slate-700">
                      <td colSpan={2 + competitors.length} className="p-2.5">
                        🔬 四、商品成分與定位內容
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">主要成分</td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {getProposalField(proposal, 'ingredients') ||
                          getProposalField(proposal, 'ingredient_text') ||
                          '依新品標示'}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.ingredient_raw_text || (c.ingredients_json ? '已結構化建檔' : '—')}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">主要特色</td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {getProposalField(proposal, 'product_features') ||
                          getProposalField(proposal, 'features') ||
                          '—'}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.features || '既有熱銷品項'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="p-3 font-bold text-slate-500 bg-slate-50/50">適用客群</td>
                      <td className="p-3 bg-teal-50/20 border-l border-r border-teal-100">
                        {getProposalField(proposal, 'target_audience') ||
                          getProposalField(proposal, 'target_customers') ||
                          '一般大眾'}
                      </td>
                      {competitors.map((c) => (
                        <td
                          key={c.id}
                          className={`p-3 ${c.is_primary ? 'bg-amber-50/20 border-l border-r border-amber-100' : ''}`}
                        >
                          {c.target_audience || '既有客群'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
          </Card>

          {/* SECTION 5: 競品整體摘要（決策輔助文字） */}
          {multiSummaryText && (
            <Card className="p-5 bg-amber-50/60 border-amber-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1.5 flex items-center gap-1.5">
                <span>🤖 系統多競品決策輔助摘要</span>
                <span className="text-[10px] font-normal text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                  由 1~5 項商品自動綜合試算
                </span>
              </h4>
              <p className="text-sm font-medium text-amber-950 leading-relaxed">
                {multiSummaryText}
              </p>
            </Card>
          )}

          {/* SECTION 6: 公開通路網路查價觀測紀錄 */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-xs font-bold text-sky-700 uppercase">
                  SOURCE C: 網路公開與外部市場價格
                </span>
                <h3 className="text-base font-bold text-ink">外部通路查價觀測紀錄</h3>
              </div>
              {comparisonData?.web_price_stats.count ? (
                <div className="flex gap-4 text-xs font-mono bg-sky-50 px-3 py-1.5 rounded-xl border border-sky-100">
                  <span>
                    最低: <Money value={comparisonData.web_price_stats.min_price} />
                  </span>
                  <span>
                    中位: <Money value={comparisonData.web_price_stats.median_price} />
                  </span>
                  <span>
                    最高: <Money value={comparisonData.web_price_stats.max_price} />
                  </span>
                  <span>
                    均價: <Money value={comparisonData.web_price_stats.avg_price} />
                  </span>
                </div>
              ) : null}
            </div>

            {comparisonData?.price_observations && comparisonData.price_observations.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <Table>
                  <thead>
                    <tr className="bg-slate-50 text-slate-700 text-xs">
                      <th className="p-3 text-left">通路名稱</th>
                      <th className="p-3 text-left">商品名稱與規格</th>
                      <th className="p-3 text-right">公開售價</th>
                      <th className="p-3 text-right">促銷價／搭贈</th>
                      <th className="p-3 text-left">來源資訊</th>
                      <th className="p-3 text-center">採購採納</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {comparisonData.price_observations.map((obs) => (
                      <tr key={obs.id} className="hover:bg-slate-50/60">
                        <td className="p-3 font-bold text-ink">{obs.channel_name}</td>
                        <td className="p-3">
                          <div className="font-medium">{obs.product_name}</div>
                          <div className="text-slate-400 text-[11px]">{obs.specification}</div>
                        </td>
                        <td className="p-3 text-right font-bold text-ink">
                          <Money value={obs.public_price} />
                        </td>
                        <td className="p-3 text-right">
                          {obs.promo_price ? (
                            <span className="font-bold text-rose-700">
                              <Money value={obs.promo_price} />
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                          {obs.giveaway_terms && (
                            <div className="text-[11px] text-emerald-700">{obs.giveaway_terms}</div>
                          )}
                        </td>
                        <td className="p-3 text-slate-500">
                          <div>{obs.source_name}</div>
                          {obs.source_url && (
                            <a
                              href={obs.source_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] text-teal-700 hover:underline inline-block mt-0.5"
                            >
                              前往來源連結 ↗
                            </a>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {obs.buyer_confirmed ? (
                            <button
                              type="button"
                              onClick={() => void handleToggleWebPrice(obs.id, false)}
                              className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded font-bold hover:bg-emerald-200 transition"
                            >
                              ✓ 已確認採納
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void handleToggleWebPrice(obs.id, true)}
                              className="px-2 py-1 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition"
                            >
                              待確認
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400 text-xs">
                目前尚無網路查價觀測紀錄。
              </div>
            )}
          </Card>

          {/* SECTION 7: 採購比較結論與決策建議 */}
          <Card className="p-6 border-teal-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-5">
              <div>
                <h3 className="text-lg font-extrabold text-ink">採購比較結論與建議</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  完成 1～5 個商品比較後填寫；此結論將同步帶入「新品會議模式」供決策審查。
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateSnapshot} loading={snapshotting} variant="secondary">
                  📸 建立比較快照
                </Button>
                <Button onClick={handleSaveConclusion} loading={savingConclusion}>
                  💾 儲存比較結論
                </Button>
              </div>
            </div>

            <div className="space-y-4 text-xs">
              {/* 四項指標評估 */}
              <div className="grid gap-3 sm:grid-cols-4 p-3 bg-slate-50 rounded-xl">
                <label className="flex items-center gap-2 font-bold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPriceComp}
                    onChange={(e) => setIsPriceComp(e.target.checked)}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>價格具競爭力</span>
                </label>
                <label className="flex items-center gap-2 font-bold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isIngredientSup}
                    onChange={(e) => setIsIngredientSup(e.target.checked)}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>成分／規格優勢</span>
                </label>
                <label className="flex items-center gap-2 font-bold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isGiveawaySup}
                    onChange={(e) => setIsGiveawaySup(e.target.checked)}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>搭贈條件具吸引力</span>
                </label>
                <label className="flex items-center gap-2 font-bold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isMarginSup}
                    onChange={(e) => setIsMarginSup(e.target.checked)}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>毛利率符合門檻</span>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">新品主要優勢：</label>
                  <textarea
                    className="w-full rounded-xl border border-slate-300 p-3 text-xs text-ink focus:border-teal-500 focus:outline-none"
                    rows={3}
                    placeholder="請條列新品之核心價格、成分、包裝或促銷優勢..."
                    value={advantages}
                    onChange={(e) => setAdvantages(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    新品主要劣勢與風險：
                  </label>
                  <textarea
                    className="w-full rounded-xl border border-slate-300 p-3 text-xs text-ink focus:border-teal-500 focus:outline-none"
                    rows={3}
                    placeholder="請評估新品之退貨限制、效期、既有商品庫存排擠等風險..."
                    value={disadvantages}
                    onChange={(e) => setDisadvantages(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">退換貨風險與判斷：</label>
                  <textarea
                    className="w-full rounded-xl border border-slate-300 p-3 text-xs text-ink focus:border-teal-500 focus:outline-none"
                    rows={3}
                    placeholder="例如：新品不可退貨；效期剩餘 6 個月內僅可換貨，風險較高。"
                    value={returnRiskNotes}
                    onChange={(event) => setReturnRiskNotes(event.target.value)}
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    既有庫存／取代風險：
                  </label>
                  <textarea
                    className="w-full rounded-xl border border-slate-300 p-3 text-xs text-ink focus:border-teal-500 focus:outline-none"
                    rows={3}
                    placeholder="例如：主要競品庫存 128 盒、已訂未到 24 盒，需先規劃促銷去化。"
                    value={inventoryRiskNotes}
                    onChange={(event) => setInventoryRiskNotes(event.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3 items-center pt-2">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">採購建議：</label>
                  <Select value={replacement} onChange={(e) => setReplacement(e.target.value)}>
                    {Object.entries(COMPARISON_REPLACEMENT_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="block font-bold text-slate-700 mb-1">採購建議補充說明：</label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-slate-300 px-3.5 py-2 text-xs text-ink focus:border-teal-500 focus:outline-none"
                    placeholder="例如：建議取代主要競品，舊品現存 128 盒採促銷降價出清..."
                    value={conclusionNotes}
                    onChange={(e) => setConclusionNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
